import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useDebounce from '../../hooks/useDebounce';
import { Edit2, Trash2, Eye, ChevronLeft, ChevronRight, Calendar, List, LayoutGrid, PhoneCall, X, CheckCircle, Zap, Activity, Search } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AddLead from './AddLead';
import TakeActionModal from './TakeActionModal';
import LeadPipeline from './components/LeadPipeline';
import Dropdown from '../../components/Dropdown';
import DateFilter from '../../components/DateFilter';
import PrinterLoader from '../../components/PrinterLoader';
import { isDateInRange, formatAppDate, formatAppDateOnly, formatAppTime } from '../../utils/dateUtils';
import { sortStatuses, getStatusColors } from '../../utils/statusUtils';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const LeadList = () => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [deleteLeadId, setDeleteLeadId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [selectedLeads, setSelectedLeads] = useState([]);
  
  // Pagination & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('this_month');
  const [followUpDateFilter, setFollowUpDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState(null);
  const [customFollowUpStartDate, setCustomFollowUpStartDate] = useState('');
  const [customFollowUpEndDate, setCustomFollowUpEndDate] = useState(null);
  
  // Follow-up quick action states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpLead, setFollowUpLead] = useState(null);
  const [followUpDate, setFollowUpDate] = useState(null);
  const [followUpTime, setFollowUpTime] = useState('');
  const [followUpNotes, setFollowUpNotes] = useState('');
  
  // Take Action states
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionLead, setActionLead] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [actionOutcome, setActionOutcome] = useState('Completed');
  const [actionDate, setActionDate] = useState(null);
  const [actionTime, setActionTime] = useState('');
  const [actionDaysToAdd, setActionDaysToAdd] = useState('');
  
  const handleActionDaysChange = (val) => {
    setActionDaysToAdd(val);
    if (val && !isNaN(val)) {
      const d = new Date();
      d.setDate(d.getDate() + parseInt(val, 10));
      setActionDate(d);
    } else {
      setActionDate(null);
    }
  };

  const handleActionDateChange = (date) => {
    setActionDate(date);
    if (date) {
      const today = new Date();
      today.setHours(0,0,0,0);
      const sel = new Date(date);
      sel.setHours(0,0,0,0);
      const diffTime = Math.abs(sel - today);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setActionDaysToAdd(diffDays.toString());
    } else {
      setActionDaysToAdd('');
    }
  };
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [statusesRes, usersRes, sourcesRes] = await Promise.all([
          api.get('/masters/status'),
          api.get('/auth/users'),
          api.get('/masters/lead-sources')
        ]);
        setStatuses(sortStatuses(statusesRes.data.filter(s => s.status === 'Active')));
        setUsersList(usersRes.data.filter(u => u.status === 'Active'));
        setSourcesList(sourcesRes.data.filter(s => s.status === 'Active'));
      } catch (err) {
        console.error('Failed to fetch filter data', err);
      }
    };
    fetchFilterData();
  }, []);


  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const state = location.state || {};
    
    const targetStatus = state.statusFilter !== undefined ? state.statusFilter : qp.get('status');
    const targetSource = state.sourceFilter !== undefined ? state.sourceFilter : qp.get('source');
    const targetDate = state.dateFilter !== undefined ? state.dateFilter : qp.get('date');
    const targetFollowUpDate = state.followUpDateFilter !== undefined ? state.followUpDateFilter : qp.get('followUpDate');
    const targetCustomerType = state.customerTypeFilter !== undefined ? state.customerTypeFilter : qp.get('customerType');

    console.log("targetStatus", targetStatus)

    let changed = false;
    if (targetStatus !== null && targetStatus !== undefined) {
      setStatusFilter(targetStatus);
      changed = true;
    }
    if (targetSource !== null && targetSource !== undefined) {
      setSourceFilter(targetSource);
      changed = true;
    }
    if (targetDate !== null && targetDate !== undefined) {
      setDateFilter(targetDate);
      changed = true;
    }
    if (targetFollowUpDate !== null && targetFollowUpDate !== undefined) {
      setFollowUpDateFilter(targetFollowUpDate);
      changed = true;
    }
    if (targetCustomerType !== null && targetCustomerType !== undefined) {
      setCustomerTypeFilter(targetCustomerType);
      changed = true;
    }
    if (changed) {
      setCurrentPage(1);
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (location.state?.openLeadId && !loading && leads.length > 0) {
      const leadToOpen = leads.find(l => l.id === location.state.openLeadId);
      if (leadToOpen) {
        setIsViewMode(true);
        setEditingLead(leadToOpen);
        setShowModal(true);
        
        // Clear state so it doesn't reopen on refresh
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, loading, leads, navigate]);

  const getTimeDate = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    return d;
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage
      });
      
      if (debouncedSearchTerm) queryParams.append('search', debouncedSearchTerm);
      if (statusFilter) queryParams.append('status', statusFilter);
      if (sourceFilter) queryParams.append('source', sourceFilter);
      if (userFilter) queryParams.append('assignee', userFilter);
      if (customerTypeFilter) queryParams.append('customerType', customerTypeFilter);
      
      const res = await api.get(`/leads?${queryParams.toString()}`);
      
      let dataToFormat = res.data.data || res.data;
      
      const formattedLeads = dataToFormat.map(lead => ({
        id: lead.id,
        name: lead.companyName,
        companyName: lead.companyName,
        userId: lead.userId,
        contact: lead.contactPerson,
        contactPerson: lead.contactPerson,
        phone: lead.phone,
        email: lead.email,
        contactPhone: lead.contactPhone,
        contactEmail: lead.contactEmail,
        frontCard: lead.frontCard,
        backCard: lead.backCard,
        address: lead.address,
        address2: lead.address2,
        city: lead.city,
        state: lead.state,
        pincode: lead.pincode,
        latitude: lead.latitude,
        longitude: lead.longitude,
        agencyType: lead.agencyType,
        machine: lead.machine?.name ? `${lead.machine.name}${lead.machineModel ? ' - ' + lead.machineModel : ''}` : 'Unknown',
        machineBrand: lead.machine?.name || '',
        machineId: lead.machineId || lead.machine?.id || null,
        machineModel: lead.machineModel || '',
        type: lead.interestType || (lead.leadType?.name && lead.leadType.name.toLowerCase() !== 'copy shop' ? lead.leadType.name : 'Unknown'),
        interestType: lead.interestType || (lead.leadType?.name && lead.leadType.name.toLowerCase() !== 'copy shop' ? lead.leadType.name : ''),
        interestedType: lead.interestType || (lead.leadType?.name && lead.leadType.name.toLowerCase() !== 'copy shop' ? lead.leadType.name : ''),
        leadType: lead.leadType,
        leadTypeId: lead.leadTypeId || lead.leadType?.id || null,
        otherInterest: lead.otherInterest || '',
        otherAgencyType: lead.otherAgencyType || '',
        machineType: lead.machineType || '',
        assignee: lead.user?.name || 'Unassigned',
        status: lead.status?.name || 'Pending',
        source: lead.leadSource?.name || 'Unknown',
        referenceName: lead.referenceName || '',
        socialMediaPlatform: lead.socialMediaPlatform || '',
        customerType: lead.customerType?.name || null,
        createdAt: lead.createdAt,
        createdBy: lead.createdBy || 'Unknown',
        notes: lead.notes,
        followups: lead.followups || [],
        nextFollowUp: (['complete', 'completed', 'closed', 'lost', 'cancelled', 'cancel'].includes((lead.status?.name || lead.status || '').toLowerCase()))
          ? null 
          : (lead.followups && lead.followups.length > 0 
            ? [...lead.followups]
                .filter(f => f.type !== 'System' && !f.type?.toLowerCase().includes('cancel') && !f.notes?.toLowerCase().includes('cancel') && !f.notes?.startsWith('[Cancel') && !f.notes?.startsWith('[Completed]') && !f.notes?.startsWith('[Rescheduled]') && !f.notes?.startsWith('[Closed]'))
                .sort((a,b) => new Date(a.date) - new Date(b.date))[0]?.date 
            : null)
      }));
      setLeads(formattedLeads);
      
      if (res.data.data) {
        setTotalPages(res.data.totalPages || 1);
        setTotalLeads(res.data.total || 0);
      } else {
        setTotalPages(1);
        setTotalLeads(res.data.length);
      }
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, statusFilter, sourceFilter, userFilter, customerTypeFilter]);

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setIsViewMode(false);
    setShowModal(true);
  };

  const handleView = (lead) => {
    setEditingLead(lead);
    setIsViewMode(true);
    setShowModal(true);
  };
  
  const handleFollowUpClick = (lead) => {
    setFollowUpLead(lead);
    setFollowUpDate(null);
    setFollowUpTime('');
    setFollowUpNotes('');
    setShowFollowUpModal(true);
  };

  const handleActionClick = (lead) => {
    setActionLead(lead);
    setActionOutcome(lead.status?.name || lead.status || statuses[0]?.name || 'Completed');
    setActionDate(null);
    setActionTime('');
    setActionNotes('');
    setShowActionModal(true);
  };

  const handleFollowUpSubmit = async (e) => {
    e.preventDefault();
    if (!followUpLead || !followUpDate || !followUpTime) {
      toast.error("Please select a date and time");
      return;
    }
    try {
      const d = new Date(followUpDate);
      const [hours, minutes] = followUpTime.split(':');
      d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      await api.post('/followups', {
        leadId: followUpLead.id,
        date: d.toISOString(),
        notes: followUpNotes || 'Quick follow-up scheduled',
        type: 'Call'
      });
      toast.success("Follow-up scheduled successfully");
      setShowFollowUpModal(false);
      fetchLeads();
    } catch (err) {
      console.error(err);
      toast.error("Failed to schedule follow-up");
    }
  };
  
  const handleTakeActionClick = (lead) => {
    setActionLead(lead);
    setActionNotes('');
    setActionOutcome('Completed');
    setActionDate(null);
    setActionTime('');
    setShowActionModal(true);
  };

  const handleAdd = () => {
    setEditingLead(null);
    setIsViewMode(false);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteLeadId(id);
  };

  const confirmDelete = async () => {
    if (deleteLeadId) {
      try {
        await api.delete(`/leads/${deleteLeadId}`);
        toast.success('Lead deleted successfully');
        setDeleteLeadId(null);
        fetchLeads();
      } catch (error) {
        toast.error('Failed to delete lead');
        console.error(error);
      }
    }
  };

  const handleCloseModal = (shouldRefresh) => {
    setShowModal(false);
    setEditingLead(null);
    setIsViewMode(false);
    if (shouldRefresh === true) {
      fetchLeads();
    }
  };

  // Filter Logic
  const userRole = (user?.role || '').toLowerCase();
  const canViewAssignee = !userRole.includes('market') && !userRole.includes('delivery');
  const roleFilteredLeads = (user?.role === 'Admin' || hasPermission('leads:read_all')) ? leads : leads.filter(l => l.assignee === user?.name || l.createdBy === user?.name);

  const filteredLeads = roleFilteredLeads.filter(lead => {
    const matchesDate = isDateInRange(lead.createdAt, dateFilter, customStartDate, customEndDate);
    const matchesFollowUpDate = isDateInRange(lead.nextFollowUp, followUpDateFilter, customFollowUpStartDate, customFollowUpEndDate);
    const matchesStatus = !statusFilter || (lead.status || '').toLowerCase() === statusFilter.toLowerCase();
    const matchesSource = !sourceFilter || (lead.source || '').toLowerCase() === sourceFilter.toLowerCase();
    const matchesUser = !userFilter || (userFilter === 'Unassigned' ? (!lead.userId || lead.assignee === 'Unassigned') : (lead.assignee || '').toLowerCase() === userFilter.toLowerCase());
    const matchesCustomerType = !customerTypeFilter || customerTypeFilter === 'all' || (lead.customerType || '').toLowerCase() === customerTypeFilter.toLowerCase();
    return matchesDate && matchesFollowUpDate && matchesStatus && matchesSource && matchesUser && matchesCustomerType;
  });

  // Sort Logic (Newest First)
  const paginatedLeads = [...filteredLeads].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(paginatedLeads.map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (id) => {
    setSelectedLeads(prev => 
      prev.includes(id) ? prev.filter(lId => lId !== id) : [...prev, id]
    );
  };

  const handleBulkReassign = () => {
    alert(`Re-assigning leads: ${selectedLeads.join(', ')}`);
    setSelectedLeads([]);
  };

  const handleBulkDelete = () => {
    if(window.confirm('Are you sure you want to delete selected leads?')) {
      alert(`Deleted leads: ${selectedLeads.join(', ')}`);
      setSelectedLeads([]);
    }
  };

  const handleExportCSV = () => {
    if (filteredLeads.length === 0) {
      toast.error('No leads to export');
      return;
    }
    const headers = ['Lead No.', 'Lead Name', 'Contact Person', 'Email', 'Phone', 'Machine Int.', 'Source', 'Assignee', 'Creation Date', 'Next Follow-up', 'Status'];
    const rows = filteredLeads.map(lead => {
      return [
        `#${lead.id}`,
        `"${(lead.companyName || '').replace(/"/g, '""')}"`,
        `"${(lead.contactPerson || '').replace(/"/g, '""')}"`,
        `"${(lead.email || '').replace(/"/g, '""')}"`,
        `"${(lead.phone || '').replace(/"/g, '""')}"`,
        `"${(lead.machine?.name || 'Unknown').replace(/"/g, '""')}"`,
        `"${(lead.leadSource?.name || 'Unknown').replace(/"/g, '""')}"`,
        `"${(lead.user?.name || 'Unassigned').replace(/"/g, '""')}"`,
        `"${formatAppDateOnly(lead.createdAt)}"`,
        `"${lead.nextFollowUp ? formatAppDateOnly(lead.nextFollowUp) : ''}"`,
        `"${(lead.status || 'New').replace(/"/g, '""')}"`
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Leads_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {loading && <PrinterLoader />}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Leads</h2>
          <p className="text-muted text-xs md:text-sm mt-0.5">View and manage all your agency leads.</p>
        </div>
        <div className="flex gap-2.5 items-center w-full sm:w-auto justify-between sm:justify-end">
          <div style={{ display: 'flex', backgroundColor: '#f1f5f9', padding: '0.2rem', borderRadius: '0.5rem', alignItems: 'center' }}>
            <button 
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.35rem 0.6rem', borderRadius: '0.375rem',
                backgroundColor: viewMode === 'list' ? 'white' : 'transparent',
                boxShadow: viewMode === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                color: viewMode === 'list' ? 'var(--color-primary)' : '#64748b',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={18} />
            </button>
            <button 
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.35rem 0.6rem', borderRadius: '0.375rem',
                backgroundColor: viewMode === 'kanban' ? 'white' : 'transparent',
                boxShadow: viewMode === 'kanban' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                color: viewMode === 'kanban' ? 'var(--color-primary)' : '#64748b',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s'
              }}
              onClick={() => setViewMode('kanban')}
              title="Pipeline View"
            >
              <LayoutGrid size={18} />
            </button>
          </div>
          {user?.permissions?.includes("leads:write") && (
            <button className="btn btn-primary whitespace-nowrap" style={{ padding: '0.45rem 1rem', fontSize: '0.85rem' }} onClick={handleAdd}>
            + Add New Lead
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {/* Unified Responsive Toolbar for Desktop & Laptop */}
        <div className="flex flex-col gap-2.5 mb-3.5">
          <div className="flex flex-wrap items-center justify-between gap-2.5">
            {/* Left: Search Input */}
            <div className="relative flex-1" style={{ maxWidth: '380px', minWidth: '220px' }}>
              <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', pointerEvents: 'none' }} />
              <input 
                type="text" 
                placeholder="Search leads by name, contact, phone..." 
                className="input-field" 
                style={{ paddingLeft: '2.25rem', paddingRight: searchTerm ? '2rem' : '0.75rem', height: '36px', fontSize: '0.825rem', width: '100%', borderRadius: '8px' }}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }} 
              />
              {searchTerm && (
                <button 
                  onClick={() => { setSearchTerm(''); setCurrentPage(1); }} 
                  style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Right: Filters & Export Actions */}
            <div className="flex flex-wrap items-center gap-2">
              <DateFilter 
                label="Created"
                value={dateFilter}
                onChange={(val) => {
                  setDateFilter(val);
                  setCurrentPage(1);
                }}
              />

              <DateFilter 
                label="Follow-up"
                value={followUpDateFilter}
                onChange={(val) => {
                  setFollowUpDateFilter(val);
                  setCurrentPage(1);
                }}
              />

              {viewMode !== 'kanban' && (
                <Dropdown 
                  style={{ width: '130px', minWidth: '115px' }}
                  value={statusFilter}
                  placeholder="All Statuses"
                  onChange={(val) => {
                    setStatusFilter(val);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: '', label: 'All Statuses' },
                    ...statuses.map(s => ({ value: s.name, label: s.name }))
                  ]}
                />
              )}

              <Dropdown 
                style={{ width: '130px', minWidth: '115px' }}
                value={sourceFilter}
                placeholder="All Sources"
                onChange={(val) => {
                  setSourceFilter(val);
                  setCurrentPage(1);
                }}
                options={[
                  { value: '', label: 'All Sources' },
                  ...sourcesList.map(s => ({ value: s.name, label: s.name }))
                ]}
              />

              {user?.role === 'Admin' && (
                <Dropdown 
                  style={{ width: '130px', minWidth: '115px' }}
                  value={userFilter}
                  placeholder="All Users"
                  onChange={(val) => {
                    setUserFilter(val);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: '', label: 'All Users' },
                    ...usersList.map(u => ({ value: u.name, label: u.name })),
                    { value: 'Unassigned', label: 'Unassigned' },
                  ]}
                />
              )}

              <Dropdown 
                style={{ width: '130px', minWidth: '115px' }}
                value={customerTypeFilter}
                placeholder="All Priority"
                onChange={(val) => {
                  setCustomerTypeFilter(val);
                  setCurrentPage(1);
                }}
                options={[
                  { value: '', label: 'All Priority' },
                  { value: 'Hot', label: 'Hot' },
                  { value: 'Warm', label: 'Warm' },
                  { value: 'Cold', label: 'Cold' },
                ]}
              />

              {user?.role === 'Admin' && (
                <button 
                  className="btn" 
                  style={{ border: '1px solid var(--color-border)', backgroundColor: 'white', padding: '0 0.85rem', fontSize: '0.825rem', height: '36px', borderRadius: '8px', fontWeight: '500' }} 
                  onClick={handleExportCSV}
                >
                  Export
                </button>
              )}

              {(searchTerm || statusFilter || sourceFilter || userFilter || customerTypeFilter || followUpDateFilter !== 'all' || dateFilter !== 'this_month') && (
                <button
                  className="btn"
                  style={{ height: '36px', padding: '0 0.85rem', border: '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: 'white', color: 'var(--color-text-main)', cursor: 'pointer', fontSize: '0.825rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                  onClick={() => {
                    setSearchTerm('');
                    setStatusFilter('');
                    setSourceFilter('');
                    setUserFilter('');
                    setCustomerTypeFilter('');
                    setDateFilter('this_month');
                    setFollowUpDateFilter('all');
                    setCurrentPage(1);
                    navigate('/leads', { replace: true, state: {} });
                  }}
                  title="Clear all filters"
                >
                  <X size={14} /> Clear
                </button>
              )}
            </div>
          </div>
          
          {/* Custom Date Range Picker for Created */}
          {dateFilter === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100 fade-in text-xs">
              <span className="font-semibold text-muted">Created:</span>
              <input 
                type="date" 
                className="input-field" 
                style={{ height: '32px', fontSize: '0.8rem', width: 'auto', padding: '0.25rem 0.5rem' }}
                value={customStartDate}
                onChange={e => {
                  setCustomStartDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <span className="text-muted">to</span>
              <input 
                type="date" 
                className="input-field" 
                style={{ height: '32px', fontSize: '0.8rem', width: 'auto', padding: '0.25rem 0.5rem' }}
                value={customEndDate || ''}
                onChange={e => {
                  setCustomEndDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}

          {/* Custom Date Range Picker for Follow-up */}
          {followUpDateFilter === 'custom' && (
            <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100 fade-in text-xs">
              <span className="font-semibold text-muted">Follow-up:</span>
              <input 
                type="date" 
                className="input-field" 
                style={{ height: '32px', fontSize: '0.8rem', width: 'auto', padding: '0.25rem 0.5rem' }}
                value={customFollowUpStartDate}
                onChange={e => {
                  setCustomFollowUpStartDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <span className="text-muted">to</span>
              <input 
                type="date" 
                className="input-field" 
                style={{ height: '32px', fontSize: '0.8rem', width: 'auto', padding: '0.25rem 0.5rem' }}
                value={customFollowUpEndDate || ''}
                onChange={e => {
                  setCustomFollowUpEndDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
          
          {/* Bulk Actions Bar */}
          {selectedLeads.length > 0 && (
            <div style={{ backgroundColor: 'var(--color-primary-light)', padding: '0.75rem 1.5rem', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', border: '1px solid var(--color-primary)' }}>
              <div style={{ color: 'var(--color-primary-hover)', fontWeight: '600' }}>
                {selectedLeads.length} Lead(s) Selected
              </div>
              <div className="flex gap-2">
                {user?.role === 'Admin' && (
                  <button className="btn" style={{ backgroundColor: 'var(--color-danger)', color: 'white' }} onClick={handleBulkDelete}>Delete</button>
                )}
              </div>
            </div>
          )}
        </div>

        {viewMode === 'kanban' ? (
          <LeadPipeline leads={filteredLeads} onView={handleView} statuses={statuses} />
        ) : (
          <>
            <div className="table-responsive">
              <table style={{ width: '100%', minWidth: '820px', borderCollapse: 'collapse', textAlign: 'left', marginTop: 10 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                <th style={{ padding: '0.65rem 0.75rem', width: '6%', fontSize: '0.775rem' }}>Lead No.</th>
                <th style={{ padding: '0.65rem 0.75rem', width: canViewAssignee ? '14%' : '16%', fontSize: '0.775rem' }}>Lead Details</th>
                <th style={{ padding: '0.65rem 0.75rem', width: canViewAssignee ? '13%' : '15%', fontSize: '0.775rem' }}>Contact</th>
                <th style={{ padding: '0.65rem 0.75rem', width: canViewAssignee ? '13%' : '15%', fontSize: '0.775rem' }}>Product Interest</th>
                <th style={{ padding: '0.65rem 0.75rem', width: canViewAssignee ? '10%' : '12%', fontSize: '0.775rem' }}>Source</th>
                {canViewAssignee && (
                  <th style={{ padding: '0.65rem 0.75rem', width: '10%', fontSize: '0.775rem' }}>Assignee</th>
                )}
                <th style={{ padding: '0.65rem 0.75rem', width: canViewAssignee ? '11%' : '13%', fontSize: '0.775rem' }}>Created Date</th>
                <th style={{ padding: '0.65rem 0.75rem', width: canViewAssignee ? '11%' : '13%', fontSize: '0.775rem' }}>Next Follow-up</th>
                <th style={{ padding: '0.65rem 0.75rem', width: canViewAssignee ? '8%' : '10%', fontSize: '0.775rem' }}>Status</th>
                <th style={{ padding: '0.65rem 0.75rem', width: '4%', textAlign: 'center', fontSize: '0.775rem' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeads.length > 0 ? (
                paginatedLeads.map((lead, index) => (
                  <tr key={lead.id} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: selectedLeads.includes(lead.id) ? 'var(--color-primary-light)' : 'transparent' }}>
                    <td style={{ padding: '0.65rem 0.75rem' }}>
                      <button 
                        onClick={() => handleView(lead)}
                        style={{ color: 'var(--color-primary)', fontWeight: '600', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontSize: '0.825rem' }}
                        title="View Lead Details"
                      >
                        #{lead.id}
                      </button>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem' }}>
                      <div className="flex flex-col items-start gap-0.5">
                        <span style={{ fontWeight: '500', fontSize: '0.825rem' }}>{lead.name}</span>
                        {lead.customerType && (
                          <span style={{
                            padding: '0.1rem 0.35rem',
                            borderRadius: '4px',
                            backgroundColor: lead.customerType === 'Hot' ? '#ffedd5' : lead.customerType === 'Warm' ? '#fef3c7' : '#e0f2fe',
                            color: lead.customerType === 'Hot' ? '#ea580c' : lead.customerType === 'Warm' ? '#d97706' : '#0369a1',
                            fontSize: '0.625rem',
                            fontWeight: '600',
                            textTransform: 'uppercase'
                          }}>
                            {lead.customerType}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>
                      <div style={{ fontWeight: '500' }}>{lead.contact}</div>
                      <div className="text-xs mt-0.5">
                        <a href={`tel:${lead.phone}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{lead.phone}</a>
                      </div>
                      <div className="text-xs mt-0.5">
                        {lead.email && <a href={`mailto:${lead.email}`} style={{ color: 'var(--color-text-muted)', textDecoration: 'none' }}>{lead.email}</a>}
                      </div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem' }}>
                      {lead.type !== 'Unknown' && lead.type?.toLowerCase() !== 'interested in machine' && (
                        <div style={{ fontWeight: '500', color: 'var(--color-text-main)', marginBottom: '0.15rem', fontSize: '0.8rem' }}>{lead.type}</div>
                      )}
                      {lead.machineType && (
                        <div style={{ fontWeight: '600', color: 'var(--color-primary)', fontSize: '0.675rem', textTransform: 'uppercase', marginBottom: '0.15rem' }}>
                          {lead.machineType === 'rental' || lead.machineType === 'rent' ? 'Rental Machine' : lead.machineType === 'new' ? 'New Machine' : (lead.machineType === 'rc' || lead.machineType?.toLowerCase() === 'rc machine') ? 'RC Machine' : lead.machineType}
                        </div>
                      )}
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>{lead.machine !== 'Unknown' ? lead.machine : ''}</div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                      <div style={{ fontWeight: '500', color: 'var(--color-text-main)' }}>{lead.source}</div>
                      {lead.referenceName && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-primary)', fontWeight: '500' }}>
                          Ref: {lead.referenceName}
                        </div>
                      )}
                      {lead.socialMediaPlatform && (
                        <div style={{ fontSize: '0.75rem', color: '#0284c7', fontWeight: '500' }}>
                          {lead.socialMediaPlatform}
                        </div>
                      )}
                    </td>
                    {canViewAssignee && (
                      <td style={{ padding: '0.65rem 0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{lead.assignee}</td>
                    )}
                    <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>
                      <div style={{ fontWeight: '500', color: 'var(--color-text-main)', whiteSpace: 'nowrap' }}>
                        {formatAppDateOnly(lead.createdAt)}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                        {formatAppTime(lead.createdAt)}
                      </div>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', fontSize: '0.8rem' }}>
                      {lead.nextFollowUp ? (
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--color-text-main)', whiteSpace: 'nowrap' }}>
                            {formatAppDateOnly(lead.nextFollowUp)}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                            {formatAppTime(lead.nextFollowUp)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.6rem', 
                        borderRadius: '9999px', 
                        backgroundColor: getStatusColors(lead.status).bg,
                        color: getStatusColors(lead.status).text,
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        whiteSpace: 'nowrap'
                      }}>
                        {lead.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', textAlign: 'center' }}>
                      <div className="flex gap-2 justify-center">
                        {!['complete', 'completed', 'not interested', 'closed', 'won', 'lost'].includes(lead.status?.trim().toLowerCase()) && user?.permissions?.includes('followups:write') && (
                          (lead.status?.toLowerCase().includes('schedule') || lead.status?.toLowerCase().includes('follow')) ? (
                            <button className="btn text-primary" style={{ padding: '0.3rem' }} onClick={() => handleTakeActionClick(lead)} title="Take Action">
                              <Activity size={18} />
                            </button>
                          ) : (
                            <button 
                              className="btn text-primary" 
                              style={{ padding: '0.3rem' }} 
                              onClick={() => handleActionClick(lead)} 
                              title="Take Action"
                            >
                              <Activity size={18} />
                            </button>
                          )
                        )}
                        <button className="btn text-primary" style={{ padding: '0.3rem' }} onClick={() => handleView(lead)} title="View">
                          <Eye size={18} />
                        </button>
                        {user?.permissions?.includes("leads:write") && (
                          <button className="btn text-muted" style={{ padding: '0.3rem' }} onClick={() => handleEdit(lead)} title="Edit">
                            <Edit2 size={18} />
                          </button>
                        )}
                        {user?.permissions?.includes("leads:delete") && (
                          <button className="btn text-danger" style={{ padding: '0.3rem' }} onClick={() => handleDelete(lead.id)} title="Delete">
                            <Trash2 size={18} color="var(--color-danger)" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canViewAssignee ? 10 : 9} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    No leads found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {filteredLeads.length > 0 && (
          <div className="flex justify-between items-center mt-6 fade-in" style={{ padding: '0 0.5rem' }}>
            <div className="flex items-center gap-4 text-sm text-muted">
              <div>
                Showing <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min((currentPage - 1) * itemsPerPage + 1, filteredLeads.length)}</span> to <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{Math.min(currentPage * itemsPerPage, filteredLeads.length)}</span> of <span className="font-semibold" style={{ color: 'var(--color-text-main)' }}>{filteredLeads.length}</span> entries
              </div>
              <div className="flex items-center gap-2">
                <span>Show:</span>
                <Dropdown 
                  style={{ width: '80px', height: '32px' }}
                  value={itemsPerPage}
                  onChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                    { value: 50, label: '50' }
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', color: currentPage === 1 ? 'var(--color-text-muted)' : 'var(--color-text-main)', opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', borderRadius: '8px' }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button 
                  key={page} 
                  className={`btn ${currentPage === page ? 'btn-primary' : ''}`}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: currentPage === page ? 'var(--color-primary)' : 'white', 
                    border: currentPage === page ? 'none' : '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: currentPage === page ? 'white' : 'var(--color-text-main)'
                  }}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}

              <button 
                className="btn" 
                style={{ padding: '0.5rem', backgroundColor: 'white', border: '1px solid var(--color-border)', color: currentPage === totalPages || totalPages === 0 ? 'var(--color-text-muted)' : 'var(--color-text-main)', opacity: currentPage === totalPages || totalPages === 0 ? 0.5 : 1, cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer', borderRadius: '8px' }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {showModal && (
        <div className="lead-modal-overlay">
          <div className={`lead-modal-container hide-scrollbar ${isViewMode ? 'lead-profile-modal' : ''}`}>
            <AddLead 
              onClose={handleCloseModal} 
              initialData={editingLead} 
              isViewMode={isViewMode} 
              onEdit={(lead) => {
                setShowModal(false);
                setTimeout(() => handleEdit(lead), 0);
              }}
              onDelete={(id) => {
                handleDelete(id);
                // Note: handleDelete handles confirmation and its own fetch/state refresh
                setShowModal(false);
              }}
              onAction={(lead) => {
                // DO NOT close the lead profile modal!
                handleTakeActionClick(lead);
              }}
            />
          </div>
        </div>
      )}
      {showActionModal && actionLead && (
        <TakeActionModal 
          lead={actionLead} 
          onClose={() => setShowActionModal(false)}
          onSuccess={async () => {
            setShowActionModal(false);
            fetchLeads();
            if (showModal && editingLead && editingLead.id === actionLead.id) {
              try {
                const res = await api.get(`/leads/${editingLead.id}`);
                const updated = res.data;
                const isInactive = ['complete', 'completed', 'closed', 'lost', 'cancelled', 'cancel'].includes((updated.status?.name || updated.status || '').toLowerCase());
                const nextFollowUp = isInactive
                  ? null
                  : (updated.followups && updated.followups.length > 0
                    ? [...updated.followups]
                        .filter(f => f.type !== 'System' && !f.type?.toLowerCase().includes('cancel') && !f.notes?.toLowerCase().includes('cancel') && !f.notes?.startsWith('[Cancel') && !f.notes?.startsWith('[Completed]') && !f.notes?.startsWith('[Rescheduled]') && !f.notes?.startsWith('[Closed]'))
                        .sort((a,b) => new Date(a.date) - new Date(b.date))[0]?.date
                    : null);
                setEditingLead({ ...updated, nextFollowUp });
              } catch (e) {
                console.error("Failed to refresh lead data", e);
              }
            }
          }} 
        />
      )}

      {showFollowUpModal && (
        <div className="modal-backdrop" style={{ padding: '0.5rem' }}>
          <div className="modal-content fade-in" style={{ maxWidth: '440px', padding: '1.25rem' }}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold text-main">Quick Follow-up</h3>
              <button className="btn btn-secondary" style={{ padding: '4px' }} onClick={() => setShowFollowUpModal(false)}>
                <X size={18} />
              </button>
            </div>
            
            <form onSubmit={handleFollowUpSubmit}>
              <div className="mb-3">
                <label className="text-xs font-semibold mb-1 block text-muted">Date & Time *</label>
                <DatePicker
                  selected={followUpDate}
                  onChange={(date) => {
                    setFollowUpDate(date);
                    if (date) {
                      const hours = String(date.getHours()).padStart(2, '0');
                      const mins = String(date.getMinutes()).padStart(2, '0');
                      setFollowUpTime(`${hours}:${mins}`);
                    } else {
                      setFollowUpTime('');
                    }
                  }}
                  className="input-field w-full"
                  dateFormat="dd-MMM-yyyy hh:mm aa"
                  placeholderText="Select Date & Time"
                  minDate={new Date()}
                  showTimeSelect
                  timeFormat="hh:mm aa"
                  timeIntervals={15}
                  required
                  style={{ borderRadius: '8px', padding: '0.45rem 0.75rem', fontSize: '0.85rem' }}
                  minTime={
                    followUpDate && new Date(followUpDate).setHours(0,0,0,0) === new Date().setHours(0,0,0,0)
                      ? new Date()
                      : new Date(new Date().setHours(0, 0, 0, 0))
                  }
                  maxTime={new Date(new Date().setHours(23, 59, 59, 999))}
                />
              </div>
              <div className="mb-3">
                <label className="text-xs font-semibold mb-1 block text-muted">Notes</label>
                <textarea 
                  className="input-field w-full" 
                  rows="2" 
                  placeholder="E.g., Call again next week..."
                  value={followUpNotes}
                  onChange={e => setFollowUpNotes(e.target.value)}
                  style={{ resize: 'none', borderRadius: '8px', padding: '0.45rem 0.75rem', fontSize: '0.85rem' }}
                ></textarea>
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" className="btn btn-secondary" style={{ padding: '0.45rem 0.85rem', fontSize: '0.85rem' }} onClick={() => setShowFollowUpModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#10b981', color: 'white', padding: '0.45rem 0.85rem', fontSize: '0.85rem' }}>
                  Schedule Follow-up
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteLeadId && (
        <div className="modal-backdrop">
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem', textAlign: 'center' }}>
            <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Trash2 size={32} className="text-danger" />
            </div>
            <h3 className="text-xl font-bold mb-3 text-main">Delete Lead</h3>
            <p className="text-muted mb-8 text-sm">Are you sure you want to delete this lead? This action cannot be undone and all associated follow-ups will be permanently removed.</p>
            <div className="flex gap-4 justify-center">
              <button className="btn btn-secondary flex-1" onClick={() => setDeleteLeadId(null)}>
                Cancel
              </button>
              <button className="btn flex-1" onClick={confirmDelete} style={{ backgroundColor: 'var(--color-danger)', color: 'white' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadList;
